/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.model.patient.CreatePatientRequest
import studio.oddpods.cauce.core.model.validation.PhoneValidation
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_fee_invalid
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_load_failed
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_name_required
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_phone_required
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_save_failed

sealed class PatientEditEvent {
    data object SaveSuccess : PatientEditEvent()
}

sealed class PatientEditAction {
    data class NameChanged(val name: String) : PatientEditAction()
    data class PhoneChanged(val phone: String) : PatientEditAction()
    data class EmailChanged(val email: String) : PatientEditAction()
    data class FeeChanged(val fee: String) : PatientEditAction()
    data class NotesChanged(val notes: String) : PatientEditAction()
    data object SaveClicked : PatientEditAction()
}

class PatientEditViewModel(
    private val patientId: String,
    private val patientRepository: PatientRepository,
    private val currencyFormatter: CurrencyFormatter,
) : BaseViewModel<PatientFormState, PatientEditEvent, PatientEditAction>(PatientFormState(isLoading = true)) {

    init {
        loadPatient()
    }

    override fun handleAction(action: PatientEditAction) {
        when (action) {
            is PatientEditAction.NameChanged -> mutableStateFlow.value = state.copy(name = action.name, error = null)
            is PatientEditAction.PhoneChanged -> mutableStateFlow.value = state.copy(phone = action.phone, error = null)
            is PatientEditAction.EmailChanged -> mutableStateFlow.value = state.copy(email = action.email, error = null)
            is PatientEditAction.FeeChanged ->
                mutableStateFlow.value = state.copy(defaultFeeInput = action.fee, error = null)
            is PatientEditAction.NotesChanged -> mutableStateFlow.value = state.copy(notes = action.notes, error = null)
            PatientEditAction.SaveClicked -> handleSave()
        }
    }

    private fun loadPatient() {
        viewModelScope.launch {
            patientRepository.getPatient(patientId)
                .onSuccess { patient ->
                    val cents = patient.defaultFee % 100
                    val feeInput = if (cents == 0L) {
                        "${patient.defaultFee / 100}"
                    } else {
                        "${patient.defaultFee / 100},${cents.toString().padStart(2, '0')}"
                    }
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        name = patient.name,
                        phone = patient.phone,
                        email = patient.email ?: "",
                        defaultFeeInput = feeInput,
                        notes = patient.notes ?: "",
                    )
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        error = e.toUiText(Res.string.patients_error_load_failed),
                    )
                }
        }
    }

    @Suppress("ReturnCount")
    private fun handleSave() {
        val name = state.name.trim()
        val phone = state.phone.trim()
        val feeCentavos = currencyFormatter.parse(state.defaultFeeInput)

        if (name.isBlank()) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.patients_error_name_required))
            return
        }
        if (phone.isBlank()) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.patients_error_phone_required))
            return
        }
        if (feeCentavos == null || feeCentavos <= 0) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.patients_error_fee_invalid))
            return
        }

        val normalizedPhone = PhoneValidation.normalizePhone(phone)

        mutableStateFlow.value = state.copy(isSaving = true, error = null)
        viewModelScope.launch {
            patientRepository.updatePatient(
                patientId = patientId,
                request = CreatePatientRequest(
                    name = name,
                    phone = normalizedPhone,
                    email = state.email.trim().ifBlank { null },
                    defaultFee = feeCentavos,
                    notes = state.notes.trim().ifBlank { null },
                ),
            )
                .onSuccess {
                    mutableStateFlow.value = state.copy(isSaving = false)
                    sendEvent(PatientEditEvent.SaveSuccess)
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isSaving = false,
                        error = e.toUiText(Res.string.patients_error_save_failed),
                    )
                }
        }
    }
}
